import { ActionIcon, Card, Grid, Group, LoadingOverlay, Space, Stack, Text, Title, Tooltip } from "@mantine/core";
import React, { useCallback, useEffect, forwardRef, useImperativeHandle, useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { useSubscription } from '@apollo/client';
import useWebSocket from 'react-use-websocket';
import { IconRefresh } from '@tabler/icons-react';
import { buildApiUrl, buildWsUrl } from '../../../config/endpoints';
import { TOPUP_COMPLETED_SUB } from '../../../queries/OrdersAndWallets';

const AllocationCard = forwardRef(function AllocationCard({ userid, onTotalChange }, ref) {
  const [isLoading, setIsLoading] = useState(false);
  const [date, setDate] = useState("");
  const [total, setTotal] = useState(0);
  const [items, setItems] = useState([]); // [{symbol, value, pct}]

  useEffect(() => {
    const d = new Date();
    const month = ['Jan.', 'Feb.', 'Mar.', 'Apr.', 'May', 'Jun.', 'Jul.', 'Aug.', 'Sept.', 'Oct.', 'Nov.', 'Dec.'][d.getMonth()];
    setDate(d.getDate() + ' ' + month + ' ' + d.getFullYear());
  }, []);

  const allocationHandler = useCallback(async () => {
    if (!userid) return;

    setIsLoading(true);
    try {
      const response = await fetch(
        buildApiUrl('/user/allocation') + '?' +
          new URLSearchParams({ userid: userid })
      );

      if (!response.ok) {
        throw new Error(`Error! status: ${response.status}`);
      }

      const result = await response.json();

      // Normalize data types (backend might return strings/decimals)
      const rawItems = (result.items ?? []).map((x) => ({
        symbol: x.symbol,
        value: Number(x.value ?? 0),
        pct: Number(x.pct ?? 0),
      }));

      // Coinbase-like: merge tiny slices into OTHER (optional)
      const thresholdPct = 1; // 1%
      const big = rawItems.filter((x) => x.pct >= thresholdPct);
      const small = rawItems.filter((x) => x.pct < thresholdPct);

      const merged =
        small.length > 0
          ? [
              ...big,
              {
                symbol: "OTHER",
                value: small.reduce((s, x) => s + x.value, 0),
                pct: small.reduce((s, x) => s + x.pct, 0),
              },
            ]
          : big;

      const newTotal = Number(result.total ?? 0);
      setTotal(newTotal);
      onTotalChange?.(newTotal);
      setItems(merged);
    } catch (err) {
      console.error("Error fetching allocation:", err);
      return err;
    } finally {
      setIsLoading(false);
    }
  }, [userid]);

  useImperativeHandle(ref, () => ({
    refresh: () => allocationHandler(),
  }));

  // Real-time refresh on top-up completion
  useSubscription(TOPUP_COMPLETED_SUB, {
    variables: { userid: String(userid) },
    skip: !userid,
    onData: () => allocationHandler(),
  });

  // Real-time refresh on order fills via WebSocket
  const { lastMessage } = useWebSocket(buildWsUrl('/orders'), {
    shouldReconnect: () => true,
  });
  useEffect(() => {
    if (!lastMessage || !userid) return;
    try {
      const data = JSON.parse(lastMessage.data);
      if (data.type === 'ORDER_FILLED' && String(data.userID) === String(userid)) {
        allocationHandler();
      }
    } catch (e) { /* ignore parse errors */ }
  }, [lastMessage, userid, allocationHandler]);

  useEffect(() => {
    allocationHandler();
    const intervalId = setInterval(() => allocationHandler(), 60000); // refresh every minute
    return () => clearInterval(intervalId);
  }, [allocationHandler]);

  const labels = useMemo(() => items.map((x) => x.symbol), [items]);
  const series = useMemo(() => items.map((x) => x.value), [items]);

  const options = useMemo(
    () => ({
      chart: {
        type: "donut",
      },
      labels,
      legend: {
        position: "bottom",
      },
      dataLabels: {
        enabled: false,
      },
      tooltip: {
        y: {
          formatter: (val, opts) => {
            const i = opts.seriesIndex;
            const pct = items[i]?.pct ?? 0;
            return `$${Number(val).toLocaleString()} (${pct.toFixed(2)}%)`;
          },
        },
      },
      plotOptions: {
        pie: {
          donut: {
            size: "70%",
            labels: {
              show: true,
              name: { show: true },
              value: {
                show: true,
                formatter: (val) => `$${Number(val).toLocaleString()}`,
              },
              total: {
                show: true,
                label: "Total",
                formatter: () => `$${Number(total).toLocaleString()}`,
              },
            },
          },
        },
      },
      stroke: { width: 1 },
    }),
    [labels, items, total]
  );

  return (
    <Card withBorder>
      <LoadingOverlay visible={isLoading} overlayBlur={2} />
      <Stack spacing={6}>
        <Grid spacing={8}>
          <Grid.Col span={12}>
            <Group position="apart">
              <div>
                <Text fz="xs" c="dimmed">
                  Allocation
                </Text>
                <Title order={3}>${Number(total).toLocaleString()}</Title>
              </div>
              <Tooltip label="Refresh">
                <ActionIcon
                  variant="subtle"
                  onClick={() => allocationHandler()}
                  disabled={!userid || isLoading}
                  aria-label="Refresh Allocation"
                >
                  <IconRefresh size={18} />
                </ActionIcon>
              </Tooltip>
            </Group>
          </Grid.Col>
          <Grid.Col span={12} style={{ textAlign: "right" }}>
            <Text fz="xs" c="dimmed">
              As of {date}
            </Text>
          </Grid.Col>
        </Grid>

        <Space />

        {series.length === 0 ? (
          <Text size="sm" c="dimmed">
            No allocation data yet.
          </Text>
        ) : (
          <ReactApexChart
            key={series.join(',')}
            options={options}
            series={series}
            type="donut"
            height={320}
          />
        )}

        {/* Optional: show a small list like Coinbase below the donut */}
        {items.length > 0 && (
          <Stack spacing={4}>
            {items
              .slice()
              .sort((a, b) => b.value - a.value)
              .map((x) => (
                <Grid key={x.symbol} spacing={8}>
                  <Grid.Col span={4}>
                    <Text size="sm">{x.symbol}</Text>
                  </Grid.Col>
                  <Grid.Col span={4}>
                    <Text size="sm" c="dimmed">
                      {x.pct.toFixed(2)}%
                    </Text>
                  </Grid.Col>
                  <Grid.Col span={4} style={{ textAlign: "right" }}>
                    <Text size="sm">${Number(x.value).toLocaleString()}</Text>
                  </Grid.Col>
                </Grid>
              ))}
          </Stack>
        )}
      </Stack>
    </Card>
  );
});

export default AllocationCard;